const KPI_SHAPES = new Set(["single_value", "metric_with_trend", "kpi_with_trend"]);

const KPI_ROW_TEMPLATES = [
  { name: "quad", slots: [{ span: 3 }, { span: 3 }, { span: 3 }, { span: 3 }] },
  { name: "triple-equal", slots: [{ span: 4 }, { span: 4 }, { span: 4 }] },
  { name: "trend-led", slots: [{ span: 5 }, { span: 4 }, { span: 3 }] },
  { name: "wide-plus-compact", slots: [{ span: 6 }, { span: 3 }, { span: 3 }] },
  { name: "four-mixed", slots: [{ span: 4 }, { span: 3 }, { span: 3 }, { span: 2 }] },
  { name: "four-balanced", slots: [{ span: 4 }, { span: 4 }, { span: 2 }, { span: 2 }] },
  { name: "five-dense", slots: [{ span: 3 }, { span: 3 }, { span: 2 }, { span: 2 }, { span: 2 }] },
  { name: "pair-wide", slots: [{ span: 7 }, { span: 5 }] },
  { name: "pair-balanced", slots: [{ span: 6 }, { span: 6 }] },
  { name: "single", slots: [{ span: 12 }] },
];

const ANALYSIS_ROW_TEMPLATES = [
  {
    name: "primary_pair",
    slots: [
      { span: 8, slotRole: "canvas-primary", heightRole: "canvas-primary" },
      { span: 4, slotRole: "canvas-secondary", heightRole: "canvas-secondary" },
    ],
  },
  {
    name: "wide_pair",
    slots: [
      { span: 7, slotRole: "chart-medium", heightRole: "chart-medium" },
      { span: 5, slotRole: "chart-medium", heightRole: "chart-medium" },
    ],
  },
  {
    name: "balanced_pair",
    slots: [
      { span: 6, slotRole: "chart-medium", heightRole: "chart-medium" },
      { span: 6, slotRole: "chart-medium", heightRole: "chart-medium" },
    ],
  },
  {
    name: "triple_dense",
    slots: [
      { span: 4, slotRole: "chart-compact", heightRole: "chart-compact" },
      { span: 4, slotRole: "chart-compact", heightRole: "chart-compact" },
      { span: 4, slotRole: "chart-compact", heightRole: "chart-compact" },
    ],
  },
  {
    name: "table_plus_compact",
    slots: [
      { span: 8, slotRole: "table-wide", heightRole: "table-wide" },
      { span: 4, slotRole: "chart-compact", heightRole: "chart-compact" },
    ],
  },
  {
    name: "table_full",
    slots: [{ span: 12, slotRole: "table-full", heightRole: "table-full" }],
  },
  {
    name: "canvas_full",
    slots: [{ span: 12, slotRole: "canvas-primary", heightRole: "canvas-primary" }],
  },
];

const scoreHint = (hintIndex) => Math.max(0, 6 - hintIndex * 0.12);

const hasIndicator = (widget) =>
  widget?.indicator_direction !== undefined ||
  widget?.indicator_sentiment !== undefined ||
  widget?.indicator_style_key !== undefined ||
  widget?.delta_percent !== undefined ||
  widget?.comparison_display_value !== undefined;

const hasTrendData = (widget) => {
  if (Array.isArray(widget?.trend_series) && widget.trend_series.length > 1) return true;
  if (Array.isArray(widget?.chart_data?.values) && widget.chart_data.values.length > 1) return true;
  if (Array.isArray(widget?.chart_data?.labels) && widget.chart_data.labels.length > 1) return true;
  return false;
};

const getChartType = (widget) => {
  const chartType = widget?.chart_data?.chart_type;
  if (chartType === "pie") return "doughnut";
  if (chartType === "histogram") return "bar";
  return chartType || null;
};

const getSeriesCount = (widget) => {
  const chartData = widget?.chart_data || {};
  if (chartData?.series && typeof chartData.series === "object" && !Array.isArray(chartData.series)) {
    return Object.keys(chartData.series).length;
  }
  if (Array.isArray(chartData?.datasets) && chartData.datasets.length) {
    return chartData.datasets.length;
  }
  if (Array.isArray(chartData?.values) && chartData.values.length) {
    return 1;
  }
  if (widget?.expected_shape === "detail_table" && Array.isArray(widget?.table_data) && widget.table_data.length) {
    return Object.keys(widget.table_data[0] || {}).length;
  }
  return 0;
};

const getPointCount = (widget) => {
  if (Array.isArray(widget?.chart_data?.labels) && widget.chart_data.labels.length) {
    return widget.chart_data.labels.length;
  }
  if (Array.isArray(widget?.trend_series) && widget.trend_series.length) {
    return widget.trend_series.length;
  }
  if (widget?.expected_shape === "detail_table" && Array.isArray(widget?.table_data) && widget.table_data.length) {
    return widget.table_data.length;
  }
  return 0;
};

const isDistributionWidget = (widget, chartType) =>
  widget?.expected_shape === "distribution" || chartType === "doughnut";

const isTableWidget = (widget) =>
  widget?.expected_shape === "detail_table" ||
  String(widget?.widget_type || "").includes("table");

const isKpiWidget = (widget) =>
  KPI_SHAPES.has(widget?.expected_shape) ||
  String(widget?.widget_type || "").startsWith("kpi") ||
  String(widget?.widget_type || "") === "kpi" ||
  hasIndicator(widget);

const getWidgetOrder = (dashboard) => {
  const widgets = Array.isArray(dashboard?.widgets) ? dashboard.widgets : [];
  const widgetMap = new Map(widgets.map((widget) => [widget.widget_id, widget]));
  const ordered = [];

  (dashboard?.layout || []).forEach((section) => {
    (section?.widget_ids || []).forEach((widgetId) => {
      const widget = widgetMap.get(widgetId);
      if (widget) {
        ordered.push(widget);
        widgetMap.delete(widgetId);
      }
    });
  });

  widgetMap.forEach((widget) => ordered.push(widget));
  return ordered;
};

const createKpiDescriptor = (widget, hintIndex) => {
  const trend = hasTrendData(widget) || widget?.expected_shape === "metric_with_trend";
  const indicator = hasIndicator(widget);
  const titleLength = String(widget?.title || "").length;
  const pointCount = getPointCount(widget);

  return {
    widget,
    hintIndex,
    hintScore: scoreHint(hintIndex),
    isKpi: true,
    isTrendKpi: trend,
    densityScore: 1 + (indicator ? 0.5 : 0) + (trend ? 0.85 : 0) + Math.min(pointCount / 12, 0.35),
    prominenceScore: 3 + (trend ? 2.6 : 0.8) + (indicator ? 1.2 : 0) + scoreHint(hintIndex) * 0.25,
    preferredWidth: trend ? (pointCount >= 10 ? 5 : 4) : titleLength > 20 ? 3 : 2,
    minWidth: trend ? 3 : 2,
    maxWidth: trend ? 6 : 4,
    preferredHeight: trend ? "kpi-trend" : "kpi-compact",
    pairingType: trend ? "trend" : "stat",
  };
};

const createAnalysisDescriptor = (widget, hintIndex) => {
  const chartType = getChartType(widget);
  const seriesCount = getSeriesCount(widget);
  const pointCount = getPointCount(widget);
  const meta = widget?.chart_data?.meta || {};
  const isTable = isTableWidget(widget);
  const isDistribution = isDistributionWidget(widget, chartType);
  const isDualAxis = meta?.is_dual_axis === true;
  const isArea = meta?.is_area === true;
  const isStacked = meta?.is_stacked === true || meta?.stacked === true;
  const shape = widget?.expected_shape || "unknown";

  let preferredWidth = 6;
  let minWidth = 4;
  let maxWidth = 8;
  let pairingType = "balanced";
  let densityScore = 2.5;
  let prominenceScore = 4 + scoreHint(hintIndex) * 0.35;

  if (isTable) {
    preferredWidth = pointCount > 5 ? 12 : 8;
    minWidth = 8;
    maxWidth = 12;
    pairingType = "table";
    densityScore = 4.8 + Math.min(pointCount / 10, 1.2);
    prominenceScore += 2.2;
  } else if (isDistribution) {
    preferredWidth = 4;
    minWidth = 4;
    maxWidth = 5;
    pairingType = "distribution";
    densityScore = 2.2 + Math.min(seriesCount, 1);
    prominenceScore += 0.4;
  } else if (isDualAxis || isStacked || (shape === "time_series" && seriesCount >= 3) || chartType === "bubble") {
    preferredWidth = 8;
    minWidth = 6;
    maxWidth = 12;
    pairingType = "hero";
    densityScore = 5.6 + seriesCount * 0.5 + (isDualAxis ? 1.1 : 0) + (isStacked ? 0.6 : 0);
    prominenceScore += 3.3;
  } else if (shape === "time_series" || chartType === "scatter" || chartType === "line") {
    preferredWidth = seriesCount >= 2 ? 7 : 6;
    minWidth = 5;
    maxWidth = 8;
    pairingType = "wide";
    densityScore = 4.4 + seriesCount * 0.35 + (isArea ? 0.4 : 0) + Math.min(pointCount / 12, 0.6);
    prominenceScore += 2.3;
  } else if (shape === "category_ranking" || shape === "comparison" || chartType === "bar") {
    preferredWidth = seriesCount > 1 ? 6 : 4;
    minWidth = 4;
    maxWidth = 7;
    pairingType = seriesCount > 1 ? "balanced" : "compact";
    densityScore = 3.6 + seriesCount * 0.35;
    prominenceScore += seriesCount > 1 ? 1.4 : 0.7;
  }

  return {
    widget,
    hintIndex,
    hintScore: scoreHint(hintIndex),
    isKpi: false,
    isTable,
    isDistribution,
    isDualAxis,
    isArea,
    isStacked,
    shape,
    chartType,
    seriesCount,
    pointCount,
    densityScore,
    prominenceScore,
    preferredWidth,
    minWidth,
    maxWidth,
    preferredHeight: isTable ? "table" : preferredWidth >= 8 ? "tall" : preferredWidth <= 4 ? "compact" : "medium",
    pairingType,
  };
};

const getSpanMd = (span) => {
  if (span >= 8) return 6;
  if (span >= 6) return 6;
  if (span >= 4) return 3;
  return 3;
};

const getKpiSlotRole = (descriptor, span) => {
  if (descriptor.isTrendKpi) {
    return span >= 5 ? "kpi-emphasis" : "kpi-trend";
  }
  return span >= 4 ? "kpi-emphasis" : "kpi-compact";
};

const getKpiHeightRole = (descriptor, span) => {
  if (descriptor.isTrendKpi) {
    return span >= 5 ? "kpi-emphasis" : "kpi-trend";
  }
  return span >= 4 ? "kpi-compact" : "kpi-stat";
};

const scoreKpiForSlot = (descriptor, slot, rowItems) => {
  let score = descriptor.prominenceScore * 8 + descriptor.densityScore * 6;
  score -= Math.abs(slot.span - descriptor.preferredWidth) * 7;

  if (slot.span < descriptor.minWidth) {
    score -= (descriptor.minWidth - slot.span) * 30;
  }
  if (slot.span > descriptor.maxWidth) {
    score -= (slot.span - descriptor.maxWidth) * 10;
  }

  if (descriptor.isTrendKpi) {
    score += slot.span >= 4 ? 16 : -16;
    score += slot.span >= 5 ? 6 : 0;
  } else {
    score += slot.span <= 3 ? 12 : 0;
    score -= slot.span >= 6 ? 10 : 0;
  }

  const trendCount = rowItems.filter((item) => item.isTrendKpi).length;
  const statCount = rowItems.length - trendCount;

  if (!descriptor.isTrendKpi && trendCount >= 1 && slot.span <= 3) {
    score += 6;
  }
  if (descriptor.isTrendKpi && statCount >= 2 && slot.span >= 4) {
    score += 3;
  }

  return score;
};

const scoreAnalysisForSlot = (descriptor, slot, rowItems) => {
  let score = descriptor.prominenceScore * 7 + descriptor.densityScore * 6;
  score -= Math.abs(slot.span - descriptor.preferredWidth) * 5;

  if (slot.span < descriptor.minWidth) {
    score -= (descriptor.minWidth - slot.span) * 28;
  }
  if (slot.span > descriptor.maxWidth) {
    score -= (slot.span - descriptor.maxWidth) * 10;
  }

  if (descriptor.isTable) {
    score += slot.slotRole.includes("table") ? 30 : -40;
    score += slot.span >= 8 ? 8 : -12;
  } else {
    score -= slot.slotRole.includes("table") ? 40 : 0;
  }

  if (descriptor.isDistribution) {
    score += slot.slotRole === "canvas-secondary" || slot.slotRole === "distribution-compact" ? 16 : 0;
    score += slot.span <= 4 ? 12 : -10;
  }

  if (descriptor.pairingType === "hero") {
    score += slot.slotRole === "canvas-primary" ? 24 : 0;
    score += slot.span >= 7 ? 10 : -10;
  }

  if (descriptor.pairingType === "wide") {
    score += slot.slotRole === "canvas-primary" ? 10 : 0;
    score += slot.slotRole === "chart-medium" ? 8 : 0;
  }

  if (descriptor.pairingType === "compact") {
    score += slot.slotRole === "chart-compact" || slot.slotRole === "canvas-secondary" ? 12 : 0;
    score += slot.span <= 5 ? 8 : -8;
  }

  if (descriptor.chartType === "scatter" || descriptor.chartType === "bubble") {
    score += slot.span >= 5 ? 6 : -6;
  }

  const rowHasTable = rowItems.some((item) => item.isTable);
  if (descriptor.isTable && rowHasTable) {
    score -= 18;
  }

  const rowHasHero = rowItems.some((item) => item.pairingType === "hero");
  if (descriptor.pairingType === "hero" && rowHasHero && slot.span < 12) {
    score -= 9;
  }

  return score;
};

const assignTemplate = (pool, template, scorer) => {
  if (template.slots.length > pool.length) return null;

  const available = pool.map((descriptor, originalIndex) => ({ descriptor, originalIndex }));
  const slotsWithIndex = template.slots.map((slot, index) => ({ ...slot, originalSlotIndex: index }));
  slotsWithIndex.sort((left, right) => right.span - left.span);

  const rowDescriptors = [];
  let totalScore = 0;

  for (const slot of slotsWithIndex) {
    let bestCandidateIndex = -1;
    let bestScore = Number.NEGATIVE_INFINITY;

    available.forEach((candidate, candidateIndex) => {
      const candidateScore = scorer(candidate.descriptor, slot, rowDescriptors);
      if (candidateScore > bestScore) {
        bestScore = candidateScore;
        bestCandidateIndex = candidateIndex;
      }
    });

    if (bestCandidateIndex === -1) return null;

    const [{ descriptor }] = available.splice(bestCandidateIndex, 1);
    rowDescriptors.push(descriptor);
    totalScore += bestScore;
  }

  const orderedAssignments = template.slots.map((slot, slotIndex) => {
    const descriptor = rowDescriptors[slotsWithIndex.findIndex((sortedSlot) => sortedSlot.originalSlotIndex === slotIndex)];
    return {
      descriptor,
      slot,
    };
  });

  return {
    assignments: orderedAssignments,
    score: totalScore,
  };
};

const chooseBestTemplate = (pool, templates, scorer) => {
  let bestPlan = null;

  templates.forEach((template) => {
    const assignment = assignTemplate(pool, template, scorer);
    if (!assignment) return;

    const remainder = pool.length - template.slots.length;
    let score = assignment.score;

    if (remainder === 1 && pool.length > 1) score -= 18;
    if (remainder === 2 && pool.length > 4 && template.slots.length >= 4) score -= 6;
    if (template.slots.length >= 4) score += 6;
    if (template.slots.length === 1 && pool.length > 1) score -= 28;
    if (template.slots.length === 2 && pool.length >= 5) score -= 4;

    const selection = {
      template,
      assignments: assignment.assignments,
      score,
    };

    if (!bestPlan || selection.score > bestPlan.score) {
      bestPlan = selection;
    }
  });

  return bestPlan;
};

const buildKpiPlan = (descriptors) => {
  const pool = [...descriptors].sort((left, right) => {
    if (right.prominenceScore !== left.prominenceScore) return right.prominenceScore - left.prominenceScore;
    return left.hintIndex - right.hintIndex;
  });
  const plan = [];
  let rowGroup = 0;

  while (pool.length) {
    const bestRow = chooseBestTemplate(pool, KPI_ROW_TEMPLATES, scoreKpiForSlot);
    if (!bestRow) break;
    const currentRowGroup = rowGroup;

    bestRow.assignments.forEach(({ descriptor, slot }) => {
      const removeIndex = pool.findIndex((candidate) => candidate.widget.widget_id === descriptor.widget.widget_id);
      if (removeIndex !== -1) pool.splice(removeIndex, 1);

      plan.push({
        widget: descriptor.widget,
        rowGroup: currentRowGroup,
        span: slot.span,
        spanMd: getSpanMd(slot.span),
        slotRole: getKpiSlotRole(descriptor, slot.span),
        heightRole: getKpiHeightRole(descriptor, slot.span),
        densityScore: descriptor.densityScore,
      });
    });

    rowGroup += 1;
  }

  return plan;
};

const buildPrimaryAnalysisRow = (pool, rowGroup) => {
  const primaryCandidates = pool.filter((descriptor) => !descriptor.isTable);
  if (!primaryCandidates.length) return null;

  const primary = [...primaryCandidates].sort((left, right) => {
    const leftPrimaryScore = left.prominenceScore - (left.isDistribution ? 6 : 0);
    const rightPrimaryScore = right.prominenceScore - (right.isDistribution ? 6 : 0);
    if (rightPrimaryScore !== leftPrimaryScore) return rightPrimaryScore - leftPrimaryScore;
    return left.hintIndex - right.hintIndex;
  })[0];

  const primaryIndex = pool.findIndex((descriptor) => descriptor.widget.widget_id === primary.widget.widget_id);
  if (primaryIndex !== -1) pool.splice(primaryIndex, 1);

  const compactCompanion = [...pool]
    .filter((descriptor) => !descriptor.isTable)
    .sort((left, right) => {
      const leftScore =
        (left.isDistribution ? 20 : 0) +
        (left.pairingType === "compact" ? 14 : 0) +
        (left.pairingType === "wide" ? 6 : 0) +
        left.prominenceScore;
      const rightScore =
        (right.isDistribution ? 20 : 0) +
        (right.pairingType === "compact" ? 14 : 0) +
        (right.pairingType === "wide" ? 6 : 0) +
        right.prominenceScore;
      return rightScore - leftScore;
    })[0];

  const row = [
    {
      widget: primary.widget,
      rowGroup,
      span: compactCompanion ? 8 : 12,
      spanMd: compactCompanion ? 6 : 6,
      slotRole: "canvas-primary",
      heightRole: "canvas-primary",
      densityScore: primary.densityScore,
    },
  ];

  if (compactCompanion) {
    const companionIndex = pool.findIndex(
      (descriptor) => descriptor.widget.widget_id === compactCompanion.widget.widget_id
    );
    if (companionIndex !== -1) pool.splice(companionIndex, 1);

    row.push({
      widget: compactCompanion.widget,
      rowGroup,
      span: 4,
      spanMd: 3,
      slotRole: compactCompanion.isDistribution ? "distribution-compact" : "canvas-secondary",
      heightRole: compactCompanion.isDistribution ? "distribution-compact" : "canvas-secondary",
      densityScore: compactCompanion.densityScore,
    });
  }

  return row;
};

const buildAnalysisPlan = (descriptors) => {
  const pool = [...descriptors].sort((left, right) => {
    if (right.prominenceScore !== left.prominenceScore) return right.prominenceScore - left.prominenceScore;
    return left.hintIndex - right.hintIndex;
  });
  const plan = [];
  let rowGroup = 0;

  const primaryRow = buildPrimaryAnalysisRow(pool, rowGroup);
  if (primaryRow) {
    plan.push(...primaryRow);
    rowGroup += 1;
  }

  while (pool.length) {
    const candidateTemplates = ANALYSIS_ROW_TEMPLATES.filter((template) => template.slots.length <= pool.length);
    const bestRow = chooseBestTemplate(pool, candidateTemplates, scoreAnalysisForSlot);
    if (!bestRow) break;
    const currentRowGroup = rowGroup;

    bestRow.assignments.forEach(({ descriptor, slot }) => {
      const removeIndex = pool.findIndex((candidate) => candidate.widget.widget_id === descriptor.widget.widget_id);
      if (removeIndex !== -1) pool.splice(removeIndex, 1);

      const slotRole =
        descriptor.isDistribution && slot.slotRole === "chart-compact"
          ? "distribution-compact"
          : slot.slotRole;
      const heightRole =
        descriptor.isDistribution && slot.heightRole === "chart-compact"
          ? "distribution-compact"
          : slot.heightRole;

      plan.push({
        widget: descriptor.widget,
        rowGroup: currentRowGroup,
        span: slot.span,
        spanMd: getSpanMd(slot.span),
        slotRole,
        heightRole,
        densityScore: descriptor.densityScore,
      });
    });

    rowGroup += 1;
  }

  return plan;
};

export const buildDashboardLayoutPlan = (dashboard) => {
  const orderedWidgets = getWidgetOrder(dashboard);
  const descriptors = orderedWidgets.map((widget, hintIndex) =>
    isKpiWidget(widget) ? createKpiDescriptor(widget, hintIndex) : createAnalysisDescriptor(widget, hintIndex)
  );

  const kpiDescriptors = descriptors.filter((descriptor) => descriptor.isKpi);
  const analysisDescriptors = descriptors.filter((descriptor) => !descriptor.isKpi);

  return {
    orderedWidgets,
    kpiPlan: buildKpiPlan(kpiDescriptors),
    analysisPlan: buildAnalysisPlan(analysisDescriptors),
  };
};
